import { useEffect, useState } from "react";
import { AlertTriangle, Info, TriangleAlert, Cpu, RefreshCw, Sparkles, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { track } from "../../lib/analytics";

const SEV = {
  alert: { color: "text-red-300",    bg: "bg-red-500/[0.06]",    border: "border-red-500/40",    icon: AlertTriangle, label: "ALERT" },
  warn:  { color: "text-amber-300",  bg: "bg-amber-500/[0.06]",  border: "border-amber-500/40",  icon: TriangleAlert, label: "WARN" },
  info:  { color: "text-cyan-300",   bg: "bg-cyan-500/[0.06]",   border: "border-cyan-500/40",   icon: Info,          label: "INFO" },
};

function MachineCard({ m }) {
  const roiTone = m.roi_score >= 75 ? "text-emerald-400" : m.roi_score >= 50 ? "text-cyan-400" : "text-slate-400";
  return (
    <div className="corner-bracket border border-slate-800 bg-slate-950/60 p-4 rounded-sm hover:border-cyan-500/40 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Cpu size={13} className="text-cyan-400"/>
          <span className="font-mono text-xs text-slate-200 uppercase tracking-wider">{(m.type || "").replace(/_/g, " ")}</span>
        </div>
        <div className={`font-mono text-sm ${roiTone}`}>ROI {m.roi_score}</div>
      </div>
      <p className="text-xs text-slate-400 leading-snug min-h-[36px]">{m.reason}</p>
      <div className="mt-3 grid grid-cols-2 gap-2 font-mono">
        <div className="border border-slate-800 px-2 py-1.5 rounded-sm">
          <div className="text-[9px] tracking-[0.2em] uppercase text-slate-500">SPINDLE_H</div>
          <div className="text-cyan-400 text-sm">{m.est_spindle_hours.toFixed(2)} h</div>
        </div>
        <div className="border border-slate-800 px-2 py-1.5 rounded-sm">
          <div className="text-[9px] tracking-[0.2em] uppercase text-slate-500">SETUPS</div>
          <div className="text-slate-200 text-sm">{m.setup_count}</div>
        </div>
      </div>
    </div>
  );
}

function RiskRow({ r }) {
  const s = SEV[r.severity] || SEV.info;
  const Icon = s.icon;
  return (
    <div className={`border ${s.border} ${s.bg} rounded-sm px-3 py-2.5`}>
      <div className="flex items-start gap-2.5">
        <Icon size={14} className={`${s.color} mt-0.5 shrink-0`}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">[{r.code}]</div>
            <div className={`font-mono text-[10px] tracking-wider ${s.color}`}>{s.label}{r.metric ? ` · ${r.metric}` : ""}</div>
          </div>
          <div className={`mt-1 text-sm font-medium ${s.color}`}>{r.title}</div>
          <div className="mt-0.5 text-xs text-slate-400 leading-snug">{r.body}</div>
        </div>
      </div>
    </div>
  );
}

export default function EngineeringPanel({ quoteId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/quotes/${quoteId}/engineering-insights`)
      .then(({ data }) => { if (!cancelled) setData(data?.insights || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [quoteId]);

  const generate = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/quotes/${quoteId}/engineering-insights`);
      setData(data?.insights || null);
      track("engineering_insights_used", {
        quote_id: quoteId,
        risks_count: data?.insights?.risks?.length || 0,
        machines_count: data?.insights?.machine_suggestions?.length || 0,
      });
    } catch (e) {
      setError(e.response?.data?.detail || t("engPanel.genFailed"));
    }
    setBusy(false);
  };

  return (
    <div
      className="mb-6 border border-slate-800 rounded-sm bg-slate-950/60"
      data-testid="engineering-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800 bg-slate-900/40">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-live"/>
          <span className="text-cyan-400">ENG_RISK_ANALYSIS</span>
          <span className="text-slate-600">·</span>
          <span className="text-slate-500">CNC PROCESS PLANNER · CLAUDE 4.5</span>
        </div>
        <button
          onClick={generate}
          disabled={busy || loading}
          className="px-3 py-1.5 rounded-sm border border-slate-700 hover:border-cyan-500/50 hover:text-cyan-400 text-slate-300 font-mono text-[10px] tracking-wider uppercase flex items-center gap-1.5 disabled:opacity-40 transition-colors"
          data-testid="eng-generate-btn"
        >
          {busy ? <RefreshCw size={11} className="animate-spin"/> : <Sparkles size={11}/>}
          {data ? t("engPanel.regenerate") : t("engPanel.generate")}
        </button>
      </div>

      {/* Body */}
      <div className="p-4">
        {loading && (
          <div className="text-center py-8 text-slate-500 font-mono text-xs tracking-wider">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-live mr-2"/> LOADING ENGINEERING DATA...
          </div>
        )}

        {!loading && !data && (
          <div className="text-center py-8">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">// NO_ANALYSIS_YET</div>
            <p className="text-sm text-slate-400 max-w-md mx-auto leading-relaxed">
              {t("engPanel.emptyBody")}
            </p>
            <button
              onClick={generate}
              disabled={busy}
              className="mt-4 px-5 py-2.5 rounded-sm bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-mono text-xs tracking-wider uppercase flex items-center gap-2 disabled:opacity-50 mx-auto transition-colors"
              data-testid="eng-empty-cta"
            >
              {busy ? <RefreshCw size={12} className="animate-spin"/> : <Sparkles size={12}/>}
              {busy ? t("engPanel.generating") : t("engPanel.generateCta")}
            </button>
          </div>
        )}

        {error && (
          <div className="mb-3 border border-red-500/40 bg-red-500/[0.06] text-red-300 text-xs font-mono px-3 py-2 rounded-sm" data-testid="eng-error">
            [ERR] {error}
          </div>
        )}

        {data && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Risks column */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-cyan-400">PRODUCTION_RISKS</div>
                <div className="font-mono text-[10px] text-slate-500">{data.risks.length}</div>
              </div>
              <div className="space-y-2" data-testid="eng-risks">
                {data.risks.length === 0 ? (
                  <div className="text-center py-4 font-mono text-xs text-emerald-400">[NO_RISKS_DETECTED]</div>
                ) : data.risks.map((r) => <RiskRow key={r.code} r={r}/>)}
              </div>

              {data.engineer_notes && data.engineer_notes.length > 0 && (
                <div className="mt-5">
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-cyan-400 mb-3">ENGINEER_NOTES</div>
                  <ul className="space-y-1.5 font-mono text-xs text-slate-300" data-testid="eng-notes">
                    {data.engineer_notes.map((n, i) => (
                      <li key={`${n.slice(0, 20)}-${i}`} className="flex items-start gap-2">
                        <span className="text-cyan-400 mt-1">›</span>
                        <span className="leading-relaxed">{n}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Machine suggestions column */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-cyan-400 flex items-center gap-2">
                  <Wrench size={11}/> MACHINE_RECOMMENDATIONS
                </div>
                <div className="font-mono text-[10px] text-slate-500">{data.machine_suggestions.length}</div>
              </div>
              <div className="space-y-2.5" data-testid="eng-machines">
                {data.machine_suggestions.length === 0 ? (
                  <div className="text-center py-4 font-mono text-xs text-slate-500">[NO_MACHINE_DATA]</div>
                ) : data.machine_suggestions.map((m, i) => <MachineCard key={`${m.type}-${i}`} m={m}/>)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
